import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { SlideshowSelectEvent } from '@theme/events';
import { scrollIntoView } from '@theme/scrolling';

/**
 * Shared Nikura PDP media behavior layered on top of Horizon's native media-gallery.
 *
 * Correctness for "selected variant -> main image" comes from the server: Liquid
 * (eq-product-media-gallery-content.liquid) computes the selected variant's media
 * index and renders it as the slideshow's `initial-slide`, for both the first page
 * load and every fetched HTML that replaces <media-gallery> after a variant change.
 * That means the freshly-inserted gallery is already correct the moment it lands
 * in the DOM - there's nothing left for JS to race against.
 *
 * #handleProductSelect below is only a safety net for cases where the client's
 * variant/media map and the server render could disagree (e.g. stale map data).
 * It runs AFTER Horizon's native media-gallery has replaced itself, and Slideshow's
 * own select() is a no-op if the requested slide is already the current one - so in
 * the normal case this does nothing at all.
 */
class EqProductMedia extends Component {
  #controller = null;
  #desktopMedia = window.matchMedia('(min-width: 990px)');
  #variantMediaMap;

  connectedCallback() {
    super.connectedCallback();

    // Fresh controller per connection so a reconnected instance re-registers its listeners.
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const section = this.closest('.shopify-section');

    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    this.addEventListener('pointermove', this.#handlePointerMove, { signal });
    this.addEventListener('pointerleave', this.#hideHoverZoom, { signal });
    this.addEventListener('click', this.#preventDesktopNativeZoom, { capture: true, signal });

    // Slideshow sets its first disabled state during its own connect, so read it after this tick.
    requestAnimationFrame(() => {
      if (!signal.aborted) this.#syncArrowEndState();
    });

    // Variant featured images can exist in the slideshow but still be waiting on
    // network/decode work. Warm only those unique images after the initial render
    // so a later variant click can reveal them without waiting for that work.
    this.#scheduleVariantMediaPrewarm(signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /**
   * Keeps the active thumbnail visible and marked when the main slide changes.
   *
   * The gallery renders a thumbnail strip for each breakpoint, so one `slideshow-component` holds
   * two `thumbnailsContainer` elements and a `thumbnails` array covering both. Slideshow resolves
   * `thumbnailsContainer` to one of them and indexes `thumbnails` by slide number, so the pair it
   * uses can belong to different strips — it then scrolls a thumbnail that its ancestor does not
   * contain, and the second strip's thumbnails never match a slide index for `aria-selected`.
   * Each strip is handled here against its own children instead.
   */
  #handleSlideshowSelect = (event) => {
    const index = event.detail?.index;
    if (typeof index !== 'number') return;

    for (const container of this.querySelectorAll('[ref="thumbnailsContainer"]')) {
      if (!(container instanceof HTMLElement)) continue;

      const thumbnails = container.querySelectorAll('[ref="thumbnails[]"]');
      const selected = thumbnails[index];

      thumbnails.forEach((thumbnail, i) => thumbnail.setAttribute('aria-selected', `${i === index}`));

      // A hidden strip has no box to scroll, and scrolling it would move the wrong one on resize.
      if (!(selected instanceof HTMLElement) || !container.offsetParent) continue;

      // Keep thumbnail selection/highlight synchronized for every slide change, but
      // do not move the rail for programmatic selections such as variant -> media.
      // Manual slideshow/thumbnail navigation remains userInitiated and still follows.
      if (event.detail?.userInitiated !== true) continue;

      scrollIntoView(selected, { ancestor: container, behavior: 'smooth', block: 'center', inline: 'center' });
    }

    this.#syncArrowEndState();
  };

  /**
   * Mirrors the end-of-track disabled state onto every arrow.
   *
   * Same duplicate-ref situation as the thumbnails: the overlay arrows and the thumbnail
   * controls' arrows both carry `ref="previous"` / `ref="next"`, so slideshow resolves each ref
   * to one of them and disables only that one. The others keep looking enabled at the ends.
   */
  #syncArrowEndState() {
    const slideshow = this.querySelector('slideshow-component');
    if (!slideshow) return;

    const previous = slideshow.refs?.previous;
    const next = slideshow.refs?.next;
    if (!(previous instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) return;

    for (const button of this.querySelectorAll('[ref="previous"]')) {
      if (button instanceof HTMLButtonElement) button.disabled = previous.disabled;
    }
    for (const button of this.querySelectorAll('[ref="next"]')) {
      if (button instanceof HTMLButtonElement) button.disabled = next.disabled;
    }
  }

  #isDesktopHoverZoomEnabled() {
    return this.dataset.hoverZoom?.trim() === 'true' && this.#desktopMedia.matches;
  }

  #preventDesktopNativeZoom = (event) => {
    if (!this.#isDesktopHoverZoomEnabled()) return;
    if (!(event.target instanceof Element)) return;

    const imageMedia = event.target.closest('.product-media-container--image');
    if (!imageMedia || !this.contains(imageMedia)) return;

    event.preventDefault();
    event.stopPropagation();
  };

  #handlePointerMove = (event) => {
    if (!this.#isDesktopHoverZoomEnabled()) return;
    if (!(event.target instanceof Element)) return;

    const image = event.target.closest('.product-media__image');
    if (!(image instanceof HTMLImageElement) || !this.contains(image)) {
      this.#hideHoverZoom();
      return;
    }

    const zoomPanel = this.refs.hoverZoom;
    const zoomImage = this.refs.hoverZoomImage;
    const lens = this.refs.zoomLens;
    if (!(zoomPanel instanceof HTMLElement) || !(zoomImage instanceof HTMLImageElement) || !(lens instanceof HTMLElement)) {
      return;
    }

    const imageRect = image.getBoundingClientRect();
    if (imageRect.width <= 0 || imageRect.height <= 0) return;

    const x = this.#clamp((event.clientX - imageRect.left) / imageRect.width, 0, 1);
    const y = this.#clamp((event.clientY - imageRect.top) / imageRect.height, 0, 1);
    const scale = Math.max(Number(this.dataset.hoverZoomScale || 200) / 100, 1);
    const highResolutionSource = image.dataset.maxResolution || image.currentSrc || image.src;

    if (highResolutionSource && zoomImage.src !== highResolutionSource) {
      zoomImage.src = highResolutionSource;
    }

    zoomImage.style.transform = `scale(${scale})`;
    zoomImage.style.transformOrigin = `${x * 100}% ${y * 100}%`;

    const hostRect = this.getBoundingClientRect();
    const lensWidth = imageRect.width / scale;
    const lensHeight = imageRect.height / scale;
    const minLeft = imageRect.left - hostRect.left;
    const minTop = imageRect.top - hostRect.top;
    const maxLeft = minLeft + imageRect.width - lensWidth;
    const maxTop = minTop + imageRect.height - lensHeight;
    const lensLeft = this.#clamp(event.clientX - hostRect.left - lensWidth / 2, minLeft, maxLeft);
    const lensTop = this.#clamp(event.clientY - hostRect.top - lensHeight / 2, minTop, maxTop);

    lens.style.width = `${lensWidth}px`;
    lens.style.height = `${lensHeight}px`;
    lens.style.left = `${lensLeft}px`;
    lens.style.top = `${lensTop}px`;

    zoomPanel.dataset.visible = 'true';
    lens.dataset.visible = 'true';
  };

  #hideHoverZoom = () => {
    if (this.refs.hoverZoom instanceof HTMLElement) this.refs.hoverZoom.dataset.visible = 'false';
    if (this.refs.zoomLens instanceof HTMLElement) this.refs.zoomLens.dataset.visible = 'false';
  };

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (this.dataset.syncVariantMedia === 'false') return;

    this.#hideHoverZoom();

    // Use the exact variant ID emitted synchronously by Horizon's VariantPicker
    // rather than parsing it back out of the fetched HTML/resource later.
    const variantId = event.detail?.variantId;
    if (!variantId) return;

    const mediaId = this.#getVariantMediaId(String(variantId));
    if (!mediaId) return;

    // If background pre-warming has not completed yet, immediately promote the
    // selected variant image before switching the slideshow.
    this.#prioritizeMedia(mediaId);

    // Match live's immediate variant-media response when the target media is already
    // present in the current EQ gallery. The normal Horizon section fetch still runs
    // and remains authoritative for the final server-rendered gallery and PDP state.
    // If the media is intentionally not rendered, #selectMedia safely no-ops and the
    // existing promise-based path below behaves exactly as before.
    this.#selectMedia(mediaId);

    event.promise
      ?.then(() => {
        // Horizon's native media-gallery replaces <media-gallery> from this same
        // promise. Wait for that swap to commit, then check the new (already
        // server-correct) gallery. select() no-ops if we're already on this slide.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.#selectMedia(mediaId));
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[eq-product-media] Variant media update failed:', error);
        }
      });
  };

  #scheduleVariantMediaPrewarm(signal) {
    const prewarm = () => {
      if (!signal.aborted) this.#prewarmVariantMedia();
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(prewarm, { timeout: 1500 });
    } else {
      window.setTimeout(prewarm, 500);
    }
  }

  #prewarmVariantMedia() {
    const mapElement = this.querySelector('script[data-eq-variant-media-map]');
    if (!(mapElement instanceof HTMLScriptElement)) return;

    if (!this.#variantMediaMap) {
      try {
        this.#variantMediaMap = JSON.parse(mapElement.textContent || '{}');
      } catch (error) {
        console.warn('[eq-product-media] Invalid variant media map:', error);
        this.#variantMediaMap = {};
      }
    }

    const mediaIds = new Set(
      Object.values(this.#variantMediaMap || {})
        .filter(Boolean)
        .map(String),
    );

    for (const mediaId of mediaIds) {
      const image = this.#getMediaImage(mediaId);
      if (!image) continue;

      // A separate low-priority Image request uses the browser cache and does not
      // alter the visible gallery. Copy srcset/sizes so the same responsive candidate
      // the real gallery image needs is fetched ahead of interaction.
      if (!image.complete) {
        const preloader = new Image();
        preloader.decoding = 'async';
        preloader.fetchPriority = 'low';
        preloader.sizes = image.sizes;
        preloader.srcset = image.srcset;
        preloader.src = image.currentSrc || image.src;
        preloader.decode?.().catch(() => {});
      } else {
        image.decode?.().catch(() => {});
      }
    }
  }

  #prioritizeMedia(mediaId) {
    const image = this.#getMediaImage(mediaId);
    if (!image) return;

    image.loading = 'eager';
    image.fetchPriority = 'high';
    image.decode?.().catch(() => {});
  }

  #getMediaImage(mediaId) {
    const gallery = this.querySelector('media-gallery');
    if (!gallery) return undefined;

    const image = gallery.querySelector(
      `slideshow-component .product-media[data-media-id="${mediaId}"] .product-media__image`,
    );

    return image instanceof HTMLImageElement ? image : undefined;
  }

  #getVariantMediaId(variantId) {
    if (!this.#variantMediaMap) {
      const mapElement = this.querySelector('script[data-eq-variant-media-map]');
      if (!(mapElement instanceof HTMLScriptElement)) return undefined;

      try {
        this.#variantMediaMap = JSON.parse(mapElement.textContent || '{}');
      } catch (error) {
        console.warn('[eq-product-media] Invalid variant media map:', error);
        this.#variantMediaMap = {};
      }
    }

    const mediaId = this.#variantMediaMap?.[variantId];
    return mediaId ? String(mediaId) : undefined;
  }

  #selectMedia(mediaId) {
    const gallery = this.querySelector('media-gallery');
    if (!gallery) return;

    const media = gallery.querySelector(`slideshow-component .product-media[data-media-id="${mediaId}"]`);
    const slide = media?.closest('slideshow-slide');
    if (!slide) return;

    const slides = Array.from(gallery.querySelectorAll('slideshow-component slideshow-slide'));
    const index = slides.indexOf(slide);
    if (index < 0) return;

    gallery.slideshow?.select(index, undefined, { animate: false });
  }

  #clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }
}

if (!customElements.get('eq-product-media')) {
  customElements.define('eq-product-media', EqProductMedia);
}

// Preserve Horizon's 3D-model bootstrap that was previously inline in the native gallery snippet.
window.ProductModel = window.ProductModel || {
  loadShopifyXR() {
    if (!window.Shopify?.loadFeatures) return;

    window.Shopify.loadFeatures([
      {
        name: 'shopify-xr',
        version: '1.0',
        onLoad: this.setupShopifyXR.bind(this),
      },
    ]);
  },

  setupShopifyXR(errors) {
    if (errors) return;

    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', () => this.setupShopifyXR(), { once: true });
      return;
    }

    document.querySelectorAll('[id^="ProductModelJSON-"]').forEach((modelJSON) => {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
      modelJSON.remove();
    });

    window.ShopifyXR.setupXRElements();
  },
};

window.ProductModel.loadShopifyXR();
